import { useEffect, useState, useRef } from "react";

// redux
import { useSelector, useDispatch } from "react-redux";
import { SET_NEW_TAG } from "redux/types";

// styles
import { StyledImage360 } from "./StyledImage360";

// components
import ScanEditorTag from "components/ScanEditorTag/ScanEditorTag";
import ScanViewerTag from "components/ScanViewerTag/ScanViewerTag";

// images
import { ReactComponent as Gesture } from "./images/gesture.svg";

// 背景預先解碼時，同時最多解碼幾張
const MAX_CONCURRENT_DECODES = 2;

const Image360 = ({
  imageList,
  viewer,
  rate,
  tagNavbar,
  tags,
  currentTag,
  fullscreen,
  onIndexChange,
  onDeleteTag,
}) => {
  const { scanSwitch, tagSwitch } = useSelector((store) => store.switch);
  const dispatch = useDispatch();

  // higher values make mouse less sensitive
  const pixelsPerDegree = 3;

  const [displayPanel, setDisplayPanel] = useState(0);

  const [images, setImages] = useState({
    dragging: false,
    startIndex: 0,
    dragStartIndex: 0,
  });

  const gestureElement = useRef();

  // 用 ref 持有 onIndexChange，這樣切換照片的 effect 就不必把它放進 dep array，
  // Editor 沒傳這個 prop 時也完全不會被影響
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;

  // 瀏覽器對 display:none 的圖片只下載、不解碼，要等第一次顯示才解碼，
  // 解碼完成前那一幀是空白的（第一次拖曳會閃爍），所以要先 decode() 完才切過去
  const imgRefs = useRef([]);
  // 已解碼完成、可以立即顯示的張數
  const decodedRef = useRef(new Set());
  // 正在 decode 的張數 → promise；背景預解碼與拖曳共用，同一張不會重複 decode
  const inflightRef = useRef(new Map());
  // 最新一次要求顯示的張數
  const pendingIndexRef = useRef(null);
  // 拖曳時是否已有一張在等 decode。同時只等一張，快速拖過的每一張才不會各自開始 decode
  const waitingRef = useRef(false);
  // 目前畫面上的張數；decode 完成的 callback 會用到，所以不能讀 render 當下的 images
  const shownIndexRef = useRef(0);

  // decode 失敗（例如圖片壞掉）也當作完成，避免卡住
  const decodeFrame = (index) => {
    // 接住當下的 Set／Map：imageList 被換掉的話，舊圖的結果不能寫進新清單
    const decoded = decodedRef.current;
    const inflight = inflightRef.current;
    if (decoded.has(index)) return Promise.resolve();
    if (!inflight.has(index)) {
      const done = () => {
        decoded.add(index);
        inflight.delete(index);
      };
      inflight.set(index, imgRefs.current[index].decode().then(done, done));
    }
    return inflight.get(index);
  };

  const showIndex = (index) => {
    pendingIndexRef.current = index;

    const img = imgRefs.current[index];
    // 已經在畫面上的那張（例如拖回原位）不用等
    const ready =
      index === shownIndexRef.current ||
      decodedRef.current.has(index) ||
      !img ||
      typeof img.decode !== "function";
    if (ready) {
      setImages((prev) => (prev.startIndex === index ? prev : { ...prev, startIndex: index }));
      return;
    }

    // 還沒解碼好就先停在目前這張。已經有一張在等的話，等它完成後會改看最新要求的張數
    if (waitingRef.current) return;
    waitingRef.current = true;
    const decoded = decodedRef.current;
    decodeFrame(index).then(() => {
      // 等待中 imageList 被換掉就作廢
      if (decodedRef.current !== decoded) return;
      waitingRef.current = false;
      showIndex(pendingIndexRef.current);
    });
  };

  const handleMouseDown = (e) => {
    // console.log('mouse down 事件');
    if (gestureElement.current.style.display !== "none") {
      gestureElement.current.style.display = "none";
    }
    // 關閉所有標籤面板
    if (viewer || (tagNavbar && tagSwitch)) setDisplayPanel(null);
    setImages({
      ...images,
      dragging: true,
      dragStart: e.screenX,
      dragStartIndex: images.startIndex,
    });
  };

  const updateImageIndex = (currentPosition) => {
    // console.log('update image index');
    // console.log(currentPosition);
    let numImages = imageList.length;
    const pixelsPerImage = pixelsPerDegree * (360 / numImages);

    const { dragStart, dragStartIndex } = images;
    // pixels moved
    let dx = (currentPosition - dragStart) / pixelsPerImage;
    let index = Math.floor(dx) % numImages;
    // decide whether to change the order
    index = scanSwitch ? index * -1 : index;
    // index calculate
    if (index < 0) index = numImages + index - 1;
    index = (index + dragStartIndex) % numImages;

    // 拖回目前這張也要呼叫，才能取消還在等 decode 的那張
    showIndex(index);
  };

  const handleMouseMove = (e) => {
    // console.log('mouese move 事件');
    if (images.dragging) {
      // console.log('dragging');
      updateImageIndex(e.screenX);
    }
  };

  const handleMouseUp = () => {
    // console.log('mouse up 事件');
    setImages({
      ...images,
      dragging: false,
    });
  };

  const handleDragStart = (e) => {
    // console.log('drag start 事件');
    e.preventDefault();
  };

  // imageList 一到就先把每張解碼好，讓第一次拖曳和之後一樣順
  useEffect(() => {
    // 換成新的 Set／Map，舊清單還沒完成的 decode 結果只會寫進舊的（見 decodeFrame）
    decodedRef.current = new Set();
    inflightRef.current = new Map();
    pendingIndexRef.current = null;
    waitingRef.current = false;

    // 360 照片張數多、解析度高（例如 60 張 4K），全部同時 decode 會造成 CPU／記憶體尖峰，
    // 所以限制同時解碼的張數，並從第一張往左右兩側由近到遠排，最先會拖到的先解碼好。
    // 拖到還沒輪到的張數時，showIndex 會自己插隊 decode（最多再多一張）。
    const total = imageList.length;
    const order = imageList.map((_, i) => (i % 2 ? (i + 1) / 2 : (total - i / 2) % total));
    let next = 0;
    let cancelled = false;

    const decodeNext = () => {
      if (cancelled || next >= order.length) return;
      const index = order[next];
      next += 1;
      const img = imgRefs.current[index];
      if (!img || typeof img.decode !== "function") {
        decodeNext();
        return;
      }
      decodeFrame(index).then(decodeNext);
    };

    for (let i = 0; i < MAX_CONCURRENT_DECODES; i += 1) decodeNext();

    return () => {
      cancelled = true;
    };
  }, [imageList]);

  useEffect(() => {
    shownIndexRef.current = images.startIndex;
    const imgContainerID = document.querySelectorAll(".imgContainer");

    imgContainerID.forEach((item, index) => {
      if (index === images.startIndex) {
        item.style.display = "flex";
      } else {
        item.style.display = "none";
      }
    });

    // 告訴外層目前轉到第幾張（Viewer 的標籤選單用它高亮「這張畫面上有的標籤」）
    onIndexChangeRef.current?.(images.startIndex);
  }, [images.startIndex]);

  // 以下 touch 事件為手機裝置專用
  const handleTouchStart = (e) => {
    // console.log('touch start 事件');
    // console.log(e.targetTouches[0].screenX);
    if (gestureElement.current.style.display !== "none") {
      gestureElement.current.style.display = "none";
    }
    // 關閉所有標籤面板
    if (viewer || (tagNavbar && tagSwitch)) setDisplayPanel(null);
    setImages({
      ...images,
      dragging: true,
      dragStart: e.targetTouches[0].screenX,
      dragStartIndex: images.startIndex,
    });
  };

  const handleTouchMove = (e) => {
    // console.log('touch move 事件');
    if (images.dragging) {
      // console.log('dragging');
      updateImageIndex(e.targetTouches[0].screenX);
    }
  };

  const handleTouchEnd = (e) => {
    // console.log('touch end 事件');
    // 使用 preventDefault() 停止 mouse event 的派送
    e.preventDefault();
    setImages({
      ...images,
      dragging: false,
    });
  };

  const handleDoubleClick = (e, index) => {
    // 關閉顯示標籤＆不是標籤列表時，不觸發「新增標籤」功能
    if (!tagSwitch) return;
    if (!tagNavbar) return;

    const left = ((e.nativeEvent.offsetX - 12) / e.target.width) * 100;
    const top = ((e.nativeEvent.offsetY - 12) / e.target.height) * 100;

    const createNewTag = {
      id: null,
      uniqueId: tags.length + 1,
      dataPosition: `${left} ${top}`,
      scankitIndex: index,
      title: "",
      content: "",
      icon: "",
      url: "",
    };

    // dispatch newTags 就會更新 setTags
    dispatch({ type: SET_NEW_TAG, payload: createNewTag });
  };

  useEffect(() => {
    // 還沒點過任何標籤（Viewer 初始為 null），不要在 mount 時亂動畫面
    if (!currentTag) return;
    // 關閉顯示標籤＆不是標籤列表時，不觸發「標籤快捷鍵」功能
    if (!tagSwitch) return;
    // Editor 走左側「我的標籤」列表，Viewer 走標籤選單，兩者都要能快捷跳轉
    if (!tagNavbar && !viewer) return;

    // 點擊標籤列表裡的標籤，跳到該標籤的 imageIndex
    // 用 functional form，避免蓋掉拖曳中的 dragging / dragStart（多指操作時會踩到）
    setImages((prev) => ({
      ...prev,
      dragStartIndex: currentTag.imageIndex,
    }));
    // 跟拖曳一樣，等該張解碼好才切過去
    showIndex(currentTag.imageIndex);

    // 點擊標籤列表裡的標籤，如該標籤面板已開啟則收合
    if (currentTag.serialNumber === displayPanel) {
      setDisplayPanel(null);
    }

    // 點擊標籤列表裡的標籤，如該標籤面板未開啟則展開
    if (currentTag.serialNumber !== displayPanel) {
      setDisplayPanel(currentTag.serialNumber);
    }
  }, [currentTag]);

  return (
    <StyledImage360
      $fullscreen={fullscreen}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDragStart={handleDragStart}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="allContainer">
        {imageList.map((item, index) => {
          return (
            <div key={index} className="imgContainer">
              <img
                ref={(el) => {
                  imgRefs.current[index] = el;
                }}
                src={item}
                alt="images"
                className="img360"
                style={{ transform: `scale(${rate})` }}
                onDoubleClick={(e) => handleDoubleClick(e, index)}
              />
            </div>
          );
        })}
        {/* Editor 才顯示 */}
        {tagNavbar &&
          tags.map((item, index) => {
            return (
              <div key={index} className="tagContainer">
                <ScanEditorTag
                  onDeleteTag={onDeleteTag}
                  item={item}
                  index={index}
                  images={images}
                  newTag={item.id === null}
                  currentTag={currentTag}
                  displayPanel={displayPanel}
                  setDisplayPanel={setDisplayPanel}
                />
              </div>
            );
          })}
        {/* Viewer 才顯示 */}
        {viewer &&
          tags.map((item, index) => {
            return (
              <div key={index} className="tagContainer">
                <ScanViewerTag
                  item={item}
                  index={index}
                  images={images}
                  displayPanel={displayPanel}
                  setDisplayPanel={setDisplayPanel}
                />
              </div>
            );
          })}
      </div>
      {imageList.length > 0 && <Gesture ref={gestureElement} className="gestureIcon" />}
    </StyledImage360>
  );
};

export default Image360;
